import type { AppLocale } from "./index"
import { EXTENDED_STATUS_TITLES } from "./extendedLocaleUi"
import { isExtendedLocale } from "./isExtendedLocale"
import {
  localizeStatusFrameLabel as legacyLocalizeStatusFrameLabel,
  localizeStatusTitle as legacyLocalizeStatusTitle,
} from "./legacy/statusTitles"
import type { AppLocale as LegacyAppLocale } from "./legacy/index"
import { isPublicExtraLocale } from "./publicExtraSignals"
import { PUBLIC_EXTRA_STATUS_TITLES } from "./publicExtraStatuses"
import { uzbekLatinToCyrillic } from "./uzbekCyrillic"
import { isExpansionLocale } from "./publicExpansionSignals"
import { EXPANSION_STATUS_TITLES } from "./publicExpansionStatuses"

const STATUS_ORDER = [
  "auto-lady", "taxi", "night-driver", "dacha", "new-driver", "electric", "hybrid", "gas",
  "kids", "family", "traveler", "trucker", "offroad", "speed", "sportscar", "retro", "tuning",
  "neighbor", "garage-club", "courier", "work-car", "cargo", "own-style", "autoclan",
] as const

type CanonicalStatusSlug = (typeof STATUS_ORDER)[number]

const ALIASES: Record<string, CanonicalStatusSlug> = {
  "auto-lady": "auto-lady", auto_lady: "auto-lady", autolady: "auto-lady",
  taxi: "taxi",
  "night-driver": "night-driver", night_driver: "night-driver",
  dacha: "dacha", dachnik: "dacha",
  "new-driver": "new-driver", new_driver: "new-driver", newbie: "new-driver",
  electric: "electric", hybrid: "hybrid", gas: "gas",
  kids: "kids", kids_in_car_status: "kids",
  family: "family", traveler: "traveler", trucker: "trucker",
  offroad: "offroad", suv: "offroad",
  speed: "speed", speed_lover: "speed",
  sportscar: "sportscar", retro: "retro", tuning: "tuning",
  neighbor: "neighbor", yard_neighbor: "neighbor",
  "garage-club": "garage-club", garage_club: "garage-club",
  courier: "courier",
  "work-car": "work-car", work_car: "work-car",
  cargo: "cargo",
  "own-style": "own-style", own_style: "own-style",
  autoclan: "autoclan", clan_member: "autoclan",
}

function canonicalStatusSlug(slug?: string | null): CanonicalStatusSlug | null {
  if (!slug) return null
  return ALIASES[slug.trim().toLowerCase()] ?? null
}

export function localizeStatusTitle(locale: AppLocale, slug?: string | null, fallback = ""): string {
  const canonical = canonicalStatusSlug(slug)
  if (isExpansionLocale(locale)) {
    if (!canonical) return fallback
    const index = STATUS_ORDER.indexOf(canonical)
    return EXPANSION_STATUS_TITLES[locale][index]?.[0] ?? fallback
  }
  if (isPublicExtraLocale(locale)) {
    if (!canonical) return fallback
    const index = STATUS_ORDER.indexOf(canonical)
    return PUBLIC_EXTRA_STATUS_TITLES[locale][index]?.[0] ?? fallback
  }

  if (!isExtendedLocale(locale)) {
    const value = legacyLocalizeStatusTitle(locale as LegacyAppLocale, slug, fallback)
    return locale === "uz" ? uzbekLatinToCyrillic(value) : value
  }

  if (!canonical) return fallback
  const index = STATUS_ORDER.indexOf(canonical)
  return EXTENDED_STATUS_TITLES[locale][index]?.[0] ?? fallback
}

export function localizeStatusFrameLabel(locale: AppLocale, slug?: string | null, fallback = ""): string {
  const canonical = canonicalStatusSlug(slug)
  if (isExpansionLocale(locale)) {
    if (!canonical || canonical === "autoclan") return fallback
    const index = STATUS_ORDER.indexOf(canonical)
    return EXPANSION_STATUS_TITLES[locale][index]?.[1] ?? fallback
  }
  if (isPublicExtraLocale(locale)) {
    if (!canonical || canonical === "autoclan") return fallback
    const index = STATUS_ORDER.indexOf(canonical)
    return PUBLIC_EXTRA_STATUS_TITLES[locale][index]?.[1] ?? fallback
  }

  if (!isExtendedLocale(locale)) {
    const value = legacyLocalizeStatusFrameLabel(locale as LegacyAppLocale, slug, fallback)
    return locale === "uz" ? uzbekLatinToCyrillic(value) : value
  }

  if (!canonical || canonical === "autoclan") return fallback
  const index = STATUS_ORDER.indexOf(canonical)
  return EXTENDED_STATUS_TITLES[locale][index]?.[1] ?? fallback
}
